import { useMemo } from "react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { History, Download, X } from "lucide-react";
import { API } from "@/lib/api";

const ACTION_META = {
  promote:      { label: "Promoted to admin",  className: "bg-amber-100 text-amber-900" },
  demote:       { label: "Demoted from admin", className: "bg-stone-100 text-stone-700" },
  suspend:      { label: "Suspended",          className: "bg-orange-100 text-orange-900" },
  reactivate:   { label: "Reactivated",        className: "bg-emerald-100 text-emerald-900" },
  rename:       { label: "Renamed",            className: "bg-stone-100 text-stone-700" },
  delete_user:  { label: "Deleted user",       className: "bg-red-100 text-red-900" },
  impersonate:  { label: "Impersonated",       className: "bg-amber-200 text-amber-900" },
};

function renderDetails(e) {
  const d = e.details || {};
  if (e.action === "rename" && d.from && d.to) return `"${d.from}" → "${d.to}"`;
  if (e.action === "delete_user" && d.cascade) {
    const c = d.cascade;
    const parts = Object.entries(c).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k}`);
    return parts.length ? `Cascaded: ${parts.join(", ")}` : "No related data";
  }
  if (e.action === "impersonate" && d.ttl_minutes) return `Token valid for ${d.ttl_minutes} min`;
  return "";
}

export default function AdminAuditLog({ entries, users, filters, setFilters }) {
  const actorOptions = useMemo(() => {
    const seen = new Map();
    entries.forEach((e) => seen.set(e.actor_id, { id: e.actor_id, email: e.actor_email, name: e.actor_name }));
    users.filter((u) => u.is_admin).forEach((u) => {
      if (!seen.has(u.id)) seen.set(u.id, { id: u.id, email: u.email, name: u.name });
    });
    return Array.from(seen.values());
  }, [entries, users]);

  const targetOptions = useMemo(() => users.map((u) => ({ id: u.id, email: u.email, name: u.name })), [users]);

  const update = (k, v) => setFilters((prev) => ({ ...prev, [k]: v }));
  const clearAll = () => setFilters({ action: "all", actor_id: "all", target_id: "all", from_date: "", to_date: "" });
  const anyActive =
    filters.action !== "all" ||
    filters.actor_id !== "all" ||
    filters.target_id !== "all" ||
    filters.from_date ||
    filters.to_date;

  const exportCsv = async () => {
    try {
      const qs = new URLSearchParams();
      if (filters.action !== "all") qs.set("action", filters.action);
      if (filters.actor_id !== "all") qs.set("actor_id", filters.actor_id);
      if (filters.target_id !== "all") qs.set("target_id", filters.target_id);
      if (filters.from_date) qs.set("from_date", filters.from_date);
      if (filters.to_date) qs.set("to_date", filters.to_date);
      const token = localStorage.getItem("beekeep_token");
      const res = await fetch(`${API}/admin/audit/export?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers.get("Content-Disposition") || "";
      const m = /filename="([^"]+)"/.exec(cd);
      a.download = m ? m[1] : `audit-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Audit log downloaded");
    } catch (e) {
      toast.error("Export failed: " + e.message);
    }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-6" data-testid="audit-log-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <div>
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-stone-500" />
            <h2 className="font-display text-xl font-medium">Admin activity</h2>
          </div>
          <p className="text-xs text-stone-500 mt-1">Newest first. Filter or export to CSV.</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full border-stone-300 h-8"
          onClick={exportCsv}
          data-testid="audit-export-button"
        >
          <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-4 mb-6">
        <div>
          <Label className="text-xs text-stone-500 uppercase tracking-[0.15em]">Action</Label>
          <Select value={filters.action} onValueChange={(v) => update("action", v)}>
            <SelectTrigger className="mt-1.5 h-9 text-sm" data-testid="audit-filter-action"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {Object.entries(ACTION_META).map(([key, meta]) => (
                <SelectItem key={key} value={key}>{meta.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-stone-500 uppercase tracking-[0.15em]">Actor</Label>
          <Select value={filters.actor_id} onValueChange={(v) => update("actor_id", v)}>
            <SelectTrigger className="mt-1.5 h-9 text-sm" data-testid="audit-filter-actor"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All admins</SelectItem>
              {actorOptions.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.name || a.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-stone-500 uppercase tracking-[0.15em]">Target</Label>
          <Select value={filters.target_id} onValueChange={(v) => update("target_id", v)}>
            <SelectTrigger className="mt-1.5 h-9 text-sm" data-testid="audit-filter-target"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All users</SelectItem>
              {targetOptions.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name || t.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-stone-500 uppercase tracking-[0.15em]">From</Label>
          <Input
            type="date"
            value={filters.from_date}
            onChange={(e) => update("from_date", e.target.value)}
            className="mt-1.5 h-9 text-sm"
            data-testid="audit-filter-from"
          />
        </div>
        <div>
          <Label className="text-xs text-stone-500 uppercase tracking-[0.15em]">To</Label>
          <Input
            type="date"
            value={filters.to_date}
            onChange={(e) => update("to_date", e.target.value)}
            className="mt-1.5 h-9 text-sm"
            data-testid="audit-filter-to"
          />
        </div>
      </div>

      {anyActive && (
        <button
          onClick={clearAll}
          className="text-xs text-stone-500 hover:text-stone-800 mb-3 inline-flex items-center gap-1"
          data-testid="audit-clear-filters"
        >
          <X className="w-3 h-3" /> Clear filters
        </button>
      )}

      {entries.length === 0 ? (
        <div className="text-sm text-stone-500 py-6 text-center">No admin actions match the current filters.</div>
      ) : (
        <ol className="relative border-l border-stone-200 ml-2" data-testid="audit-list">
          {entries.map((e) => {
            const meta = ACTION_META[e.action] || { label: e.action, className: "bg-stone-100 text-stone-700" };
            const detailsText = renderDetails(e);
            return (
              <li key={e.id} className="ml-4 pb-4" data-testid={`audit-${e.id}`}>
                <span className="absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full bg-yellow-400 border-2 border-white" />
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${meta.className}`}>{meta.label}</span>
                  {e.target_email && (
                    <span className="text-sm text-stone-700">
                      <span className="font-medium">{e.target_name || e.target_email}</span>
                      <span className="text-stone-400 ml-1">({e.target_email})</span>
                    </span>
                  )}
                </div>
                <div className="text-xs text-stone-500 mt-1">
                  by <span className="font-medium text-stone-700">{e.actor_email}</span>
                  {" · "}
                  {format(parseISO(e.created_at), "MMM d, yyyy · h:mm:ss a")}
                </div>
                {detailsText && <div className="text-xs text-stone-600 mt-1">{detailsText}</div>}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
